/**
 * AnswersManager
 *
 * Administrative dashboard module for previewing student simulation submissions
 * across exam modules (reading, listening, writing, speaking).
 *
 * Key capabilities:
 * - Fetch and filter submissions
 * - Preview module answers using the same learner-facing results review
 * - Delete submissions with confirmation
 *
 * UI built with shadcn/ui components and lucide-react icons.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MessageSquare, Loader2 } from "lucide-react";
import { UserSubmission } from "./answersManager/types";
import {
  fetchSubmissions as fetchSubmissionsApi,
  deleteSubmission as deleteSubmissionApi,
} from "@/lib/submissionsApi";
import { SubmissionFilters } from "./answersManager/SubmissionFilters";
import { SubmissionList } from "./answersManager/SubmissionList";
import { ModulePreviewDialog } from "./answersManager/ModulePreviewDialog";
import { DeleteConfirmationDialog } from "./answersManager/DeleteConfirmationDialog";
import { AnswerAnalysisProviderSettings } from "./answersManager/AnswerAnalysisProviderSettings";
import type { PaginatedResponse } from '@/types/pagination';
import { useModuleAnswers } from "@/hooks/useModuleAnswers";
import {
  buildPreviewSessionModules,
  getSubmissionModuleAnswerCount,
  isPreviewModuleKey,
} from "./answersManager/submissionResultAdapter";

/**
 * Admin page for previewing student submissions across modules.
 * Includes filters, submission list, learner-facing per-module preview, and deletion workflow.
 */
export default function AnswersManager() {
  const queryClient = useQueryClient();

  const [filterExamType, setFilterExamType] = useState<string>("all");
  const [filterModule, setFilterModule] = useState<string>("all");
  const [filterGraded, setFilterGraded] = useState<string>("all");
  const [filterCompletion, setFilterCompletion] = useState<string>("all");
  const [page, setPage] = useState(1);
  const PAGE_LIMIT = 10;

  // Reset to page 1 whenever any filter changes
  useEffect(() => {
    setPage(1);
  }, [filterExamType, filterModule, filterGraded, filterCompletion]);

  const submissionsQueryKey = [
    'admin-submissions',
    filterExamType,
    filterModule,
    filterGraded,
    filterCompletion,
    page,
  ] as const;

  const { data: submissionsData, isLoading, isFetching } = useQuery({
    queryKey: submissionsQueryKey,
    queryFn: () => fetchSubmissionsApi(filterExamType, filterModule, filterGraded, filterCompletion, page, PAGE_LIMIT),
    placeholderData: keepPreviousData,
  });

  const submissions = submissionsData?.items ?? [];
  const pagination = {
    page: submissionsData?.page ?? 1,
    total: submissionsData?.total ?? 0,
    totalPages: submissionsData?.totalPages ?? 0,
    limit: PAGE_LIMIT,
  };

  const [previewSubmission, setPreviewSubmission] =
    useState<UserSubmission | null>(null);
  const [previewModule, setPreviewModule] = useState<string>("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [submissionToDelete, setSubmissionToDelete] =
    useState<UserSubmission | null>(null);
  const [loadingReviewKey, setLoadingReviewKey] = useState<string | null>(null);

  const previewSessionModules = useMemo(
    () => buildPreviewSessionModules(previewSubmission),
    [previewSubmission],
  );

  const {
    answers: previewAnswers,
    isLoading: previewLoading,
    error: previewError,
    fetchAnswers,
    clearAnswers,
  } = useModuleAnswers({
    sessionModules: previewSessionModules,
    activeModule: previewModule,
    sessionId: previewSubmission?.session_id,
  });

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const deleteMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const ok = await deleteSubmissionApi(sessionId);
      if (!ok) {
        throw new Error('Failed to delete submission');
      }
      return sessionId;
    },
    onMutate: async (sessionId) => {
      await queryClient.cancelQueries({ queryKey: ['admin-submissions'] });
      const previous = queryClient.getQueryData<PaginatedResponse<UserSubmission>>(
        submissionsQueryKey,
      );
      if (previous) {
        queryClient.setQueryData(submissionsQueryKey, {
          ...previous,
          items: previous.items.filter((item) => item.session_id !== sessionId),
          total: Math.max(0, previous.total - 1),
        });
      }
      setDeleteDialogOpen(false);
      setSubmissionToDelete(null);
      return { previous };
    },
    onError: (_error, _sessionId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(submissionsQueryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-submissions'] });
    },
  });

  const handleDeleteSubmission = () => {
    if (!submissionToDelete) return;
    deleteMutation.mutate(submissionToDelete.session_id);
  };

  const deleting = deleteMutation.isPending;

  const openDeleteDialog = (submission: UserSubmission) => {
    setSubmissionToDelete(submission);
    setDeleteDialogOpen(true);
  };

  const closePreview = useCallback(() => {
    setPreviewSubmission(null);
    setPreviewModule("");
    setLoadingReviewKey(null);
    clearAnswers();
  }, [clearAnswers]);

  const handlePreviewSubmission = useCallback(async (
    submission: UserSubmission,
    module: string,
  ) => {
    if (!isPreviewModuleKey(module)) return;

    const moduleData = submission.modules.find((item) => item.module === module);
    if (!moduleData || getSubmissionModuleAnswerCount(moduleData) === 0) return;

    const reviewKey = `${submission.session_id}:${module}`;
    setLoadingReviewKey(reviewKey);
    clearAnswers();
    setPreviewSubmission(submission);
    setPreviewModule(module);

    try {
      await fetchAnswers(submission.session_id, module);
    } finally {
      setLoadingReviewKey(null);
    }
  }, [clearAnswers, fetchAnswers]);

  const handlePreviewModuleChange = useCallback(async (module: string) => {
    if (!previewSubmission || !isPreviewModuleKey(module)) return;

    const moduleData = previewSubmission.modules.find((item) => item.module === module);
    if (!moduleData || getSubmissionModuleAnswerCount(moduleData) === 0) return;

    const reviewKey = `${previewSubmission.session_id}:${module}`;
    setLoadingReviewKey(reviewKey);
    setPreviewModule(module);

    try {
      await fetchAnswers(previewSubmission.session_id, module);
    } finally {
      setLoadingReviewKey(null);
    }
  }, [fetchAnswers, previewSubmission]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-primary" />
              <p className="font-ui text-muted-foreground">
                Loading submissions...
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="font-ui flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Answers Management
              </CardTitle>
              <CardDescription className="font-ui">
                Preview student simulation submissions exactly as learners see them
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      <AnswerAnalysisProviderSettings />

      {/* Filters */}
      <SubmissionFilters
        filterExamType={filterExamType}
        setFilterExamType={setFilterExamType}
        filterModule={filterModule}
        setFilterModule={setFilterModule}
        filterGraded={filterGraded}
        setFilterGraded={setFilterGraded}
        filterCompletion={filterCompletion}
        setFilterCompletion={setFilterCompletion}
        onRefresh={() => queryClient.invalidateQueries({ queryKey: ['admin-submissions'] })}
      />

      {/* Submissions List */}
      <div className={isFetching && !isLoading ? 'opacity-70 transition-opacity' : undefined}>
        <SubmissionList
          submissions={submissions}
          total={pagination.total}
          onPreviewSubmission={handlePreviewSubmission}
          onDelete={openDeleteDialog}
          deletingSessionId={
            deleteMutation.isPending ? deleteMutation.variables ?? null : null
          }
          loadingReviewKey={loadingReviewKey}
        />
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-muted-foreground font-ui">
          Showing {submissions.length} of {pagination.total} submissions
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page <= 1 || isLoading || isFetching}
          >
            Previous
          </Button>
          <div className="flex items-center justify-center min-w-[3rem] text-sm font-medium font-ui">
            Page {pagination.page} of {Math.max(1, pagination.totalPages)}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages || isLoading || isFetching}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Learner-exact Preview Dialog */}
      <ModulePreviewDialog
        submission={previewSubmission}
        moduleName={previewModule}
        answers={previewAnswers}
        isLoading={previewLoading}
        error={previewError}
        sessionModules={previewSessionModules}
        onModuleChange={handlePreviewModuleChange}
        onClose={closePreview}
        onRefreshAnswers={async () => {
          if (previewSubmission && previewModule) {
            await fetchAnswers(previewSubmission.session_id, previewModule);
          }
        }}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        submissionToDelete={submissionToDelete}
        deleting={deleting}
        onConfirm={handleDeleteSubmission}
      />
    </div>
  );
}
